/* pd-bio.js — opførsel for biomaterial-siderne.

   Tre små komponenter, hver bundet til sit eget rod-element og hver med et
   data-*-init-flag, så en gen-rendering i temaeditoren ikke binder to gange:

   1. Diagrammet  [data-bio-dia]   — GBR trin for trin: trinrækken, pilene, markørerne
      og klik direkte på et lag i tegningen skifter trin (data-step på roden). Første
      gang tegningen kommer ind i billedet, lægges lagene på ét ad gangen — ikke ved
      prefers-reduced-motion.

   2. Vælgeren    [data-bio-guide] — faner med én klinisk indikation ad gangen
      (piletaster/Home/End som i et ARIA-faneblad), fold-ud af størrelser pr. familie
      og "+ flere" pr. rolle. Indikationen kan sættes via URL'en: #indikation-<handle>.

   3. Video       [data-bio-video] — YouTube indlæses først ved klik og via
      youtube-nocookie.com, så siden ikke sætter cookies, før kunden selv vil se
      videoen (samme princip som MM-webinarerne og samtykkeløsningen).

   5. Producentfilter [data-bio-filter] — producentkortene i nøgletalsbåndet skifter
      Shopify-filteret uden genindlæsning: siden hentes med det nye filter,
      #ProductGridContainer og filterknapperne byttes ud (samme greb som
      custom_product_filters), og der scrolles ned til #produkter. Kortenes aktive
      tilstand læses altid fra URL'en, så den også følger med, når kunden nulstiller
      i filterknapperne.

   6. Galleri     [data-bio-acc]   — "Bruges ofte sammen med" på forsiden: ét panel er
      åbent ad gangen og åbnes ved hover (mus), fokus (tastatur) eller klik/tryk.

   7. Sammenligning [data-bio-cmp] — filterpillerne over tabellen på kategorisiderne
      fremhæver de passende rækker og dæmper resten; rækkerne glider ind én gang.

   Al tekst kommer fra Liquid (locale-nøgler eller metaobjekter) via data-attributter.
   Her står ingen kundevendt tekst. */

(function () {
  'use strict';

  // Filen indlæses af flere pd-bio-sektioner på samme side (én <script> pr. sektion).
  // Defer-scripts kører efter hinanden på det færdige dokument, så første kørsel ser alt;
  // de næste skal ikke binde lyttere igen.
  if (window.__pdBioInit) return;
  window.__pdBioInit = true;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- 1. Diagram ---------------- */

  // Trin 0 er overblikket (alle lag undtagen "efter heling", ingen fremhævet). Trin k viser
  // lagene med data-in <= k (og data-out > k), og laget med data-in == k er det aktuelle.
  // Alle indgange — trinrækken, pilene, markørerne og klik i selve tegningen — kalder go().
  function initDiagram(root) {
    if (root.hasAttribute('data-bio-dia-init')) return;
    root.setAttribute('data-bio-dia-init', '');

    var steps = Array.prototype.slice.call(root.querySelectorAll('[data-bio-step]'));
    var stepOf = {};
    var last = 0;
    steps.forEach(function (b) {
      var k = +b.getAttribute('data-bio-step');
      stepOf[b.getAttribute('data-bio-select')] = k;
      if (k > last) last = k;
    });
    if (!last) return;
    var parts = root.querySelectorAll('[data-in]');
    var outlines = root.querySelectorAll('[data-bio-outline]');
    var items = root.querySelectorAll('[data-bio-item]');
    var track = root.querySelector('.pd-bio-dia__track');
    var prev = root.querySelector('[data-bio-prev]');
    var nexts = root.querySelectorAll('[data-bio-next]');
    var cur = 0;
    var timers = [];

    function shown(el, s) {
      var a = +el.getAttribute('data-in');
      var b = el.hasAttribute('data-out') ? +el.getAttribute('data-out') : 99;
      if (s < 0) return false;
      if (s === 0) return a < last && b >= last;
      return a <= s && s < b;
    }

    function paint(s) {
      parts.forEach(function (el) {
        el.classList.toggle('is-off', !shown(el, s));
        el.classList.toggle('is-cur', s > 0 && +el.getAttribute('data-in') === s);
      });
    }

    function render(s) {
      cur = s;
      root.setAttribute('data-step', String(s));
      paint(s);
      outlines.forEach(function (o) {
        o.classList.toggle('is-cur', s > 0 && +o.getAttribute('data-bio-outline') === s);
      });
      steps.forEach(function (b) {
        var k = +b.getAttribute('data-bio-step');
        b.classList.toggle('is-cur', k === s);
        b.classList.toggle('is-done', s > 0 && k < s);
        b.setAttribute('aria-current', k === s ? 'step' : 'false');
      });
      items.forEach(function (it) { it.hidden = +it.getAttribute('data-bio-item') !== s; });
      if (prev) prev.disabled = s === 0;
      nexts.forEach(function (n) { n.disabled = s === last; });
      if (track) track.style.setProperty('--prog', s > 0 ? String((s - 1) / Math.max(1, last - 1)) : '0');
    }

    function stopIntro() {
      timers.forEach(clearTimeout);
      timers = [];
    }

    function go(s) {
      stopIntro();
      render(Math.max(0, Math.min(last, s)));
    }

    root.addEventListener('click', function (e) {
      var t = e.target instanceof Element ? e.target : null;
      if (!t) return;
      if (t.closest('[data-bio-prev]')) { go(cur - 1); return; }
      if (t.closest('[data-bio-next]')) { go(cur + 1); return; }
      var hit = t.closest('[data-bio-select], [data-bio-layer]');
      if (!hit) return;
      var key = hit.getAttribute('data-bio-select') || hit.getAttribute('data-bio-layer');
      if (!(key in stepOf)) return;
      go(cur === stepOf[key] ? 0 : stepOf[key]);
    });
    root.addEventListener('keydown', function (e) {
      if (!(e.target instanceof Element) || !e.target.closest('.pd-bio-dia__track, .pd-bio-dia__nav')) return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        go(cur + (e.key === 'ArrowRight' ? 1 : -1));
        var b = root.querySelector('[data-bio-step="' + cur + '"]');
        if (b && e.target.closest('.pd-bio-dia__track')) b.focus();
      }
    });

    render(0);

    // Opbygning: første gang tegningen kommer ind i billedet, lægges lagene på ét ad gangen
    // i behandlingens rækkefølge. Uden IntersectionObserver eller med reduceret bevægelse
    // står tegningen bare færdig.
    if (!reduceMotion && 'IntersectionObserver' in window) {
      // Skjul lagene med det samme (uden at fade ud), så opbygningen starter fra bunden.
      root.classList.add('is-pre');
      paint(-1);
      void root.offsetWidth;
      root.classList.remove('is-pre');
      var started = false;
      var build = function () {
        if (started) return;
        started = true;
        io.disconnect();
        for (var k = 1; k < last; k++) {
          (function (k) { timers.push(setTimeout(function () { paint(k); }, 120 + (k - 1) * 420)); })(k);
        }
        timers.push(setTimeout(function () { render(0); }, 120 + (last - 1) * 420 + 200));
      };
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { if (en.isIntersecting) build(); });
      }, { threshold: 0.2 });
      io.observe(root);
      // Reserve: står tegningen i billedet uden at observeren har meldt sig, bygges den alligevel.
      setTimeout(function () {
        var r = root.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) build();
      }, 5000);
    }
  }

  /* ---------------- 2. Vælger (forsiden) ---------------- */

  function initGuide(root) {
    if (root.hasAttribute('data-bio-guide-init')) return;
    root.setAttribute('data-bio-guide-init', '');

    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-bio-tab]'));
    var panels = root.querySelectorAll('[data-bio-panel]');

    function show(handle, focus, push) {
      tabs.forEach(function (t) {
        var on = t.getAttribute('data-bio-tab') === handle;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        if (on && focus) t.focus();
        if (on && t.scrollIntoView && window.matchMedia('(max-width: 989px)').matches) {
          t.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
        }
      });
      panels.forEach(function (p) { p.hidden = p.getAttribute('data-bio-panel') !== handle; });
      if (push && window.history && history.replaceState) {
        history.replaceState(null, '', '#indikation-' + handle);
      }
    }

    tabs.forEach(function (t, i) {
      t.addEventListener('click', function () { show(t.getAttribute('data-bio-tab'), false, true); });
      t.addEventListener('keydown', function (e) {
        var k = e.key, n = null;
        if (k === 'ArrowDown' || k === 'ArrowRight') n = tabs[(i + 1) % tabs.length];
        else if (k === 'ArrowUp' || k === 'ArrowLeft') n = tabs[(i - 1 + tabs.length) % tabs.length];
        else if (k === 'Home') n = tabs[0];
        else if (k === 'End') n = tabs[tabs.length - 1];
        if (n) { e.preventDefault(); show(n.getAttribute('data-bio-tab'), true, true); }
      });
    });

    // Fold størrelser ud/ind pr. familie
    root.querySelectorAll('[data-bio-mini]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var li = btn.closest('.pd-bio-mini');
        var open = !li.classList.contains('is-open');
        var slot = li.querySelector('[data-bio-sizes]');
        if (open && slot && !slot.hasChildNodes()) {
          var tpl = root.querySelector('template[data-bio-sizes-tpl="' + slot.getAttribute('data-bio-sizes') + '"]');
          if (tpl) slot.appendChild(tpl.content.cloneNode(true));
        }
        li.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });

    // "+ n flere" pr. rolle
    root.querySelectorAll('[data-bio-more]').forEach(function (btn) {
      var more = btn.textContent;
      btn.addEventListener('click', function () {
        var role = btn.closest('[data-bio-role]');
        var extras = role.querySelectorAll('.is-extra');
        var opening = extras.length && extras[0].hidden;
        extras.forEach(function (x) { x.hidden = !opening; });
        btn.textContent = opening ? (btn.getAttribute('data-less') || more) : more;
      });
    });

    // Dybt link: #indikation-<handle>
    var m = window.location.hash.match(/^#indikation-([a-z0-9-]+)$/);
    if (m && root.querySelector('[data-bio-tab="' + m[1] + '"]')) {
      show(m[1], false, false);
      window.setTimeout(function () {
        root.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      }, 200);
    }
  }

  /* ---------------- 3. Video ---------------- */

  function initVideo(root) {
    if (root.hasAttribute('data-bio-video-init')) return;
    root.setAttribute('data-bio-video-init', '');
    var btn = root.querySelector('[data-bio-play]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var id = root.getAttribute('data-yt');
      var title = root.getAttribute('data-title') || '';
      if (!id) return;
      var iframe = document.createElement('iframe');
      iframe.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0';
      iframe.title = title;
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
      iframe.allowFullscreen = true;
      var frame = root.querySelector('.pd-bio-video__frame');
      frame.innerHTML = '';
      frame.appendChild(iframe);
    });
  }

  /* ---------------- 4. "+ n størrelser" (også i kopierede skabeloner) ---------------- */

  document.addEventListener('click', function (e) {
    var ib = e.target instanceof Element ? e.target.closest('[data-bio-ind-more]') : null;
    if (ib) {
      var box = ib.closest('.pd-bio-cmp__inds');
      var hid = box.querySelectorAll('.is-more');
      var op = hid.length && hid[0].hidden;
      hid.forEach(function (x) { x.hidden = !op; });
      if (!ib.hasAttribute('data-more')) ib.setAttribute('data-more', ib.textContent);
      ib.textContent = op ? ib.getAttribute('data-less') : ib.getAttribute('data-more');
      return;
    }
    var btn = e.target instanceof Element ? e.target.closest('[data-bio-sizes-more]') : null;
    if (!btn) return;
    var wrap = btn.closest('.pd-bio-sizes-wrap');
    var items = wrap.querySelectorAll('.is-more');
    var opening = items.length && items[0].hidden;
    items.forEach(function (x) { x.hidden = !opening; });
    if (!btn.hasAttribute('data-more')) btn.setAttribute('data-more', btn.textContent);
    btn.textContent = opening ? btn.getAttribute('data-less') : btn.getAttribute('data-more');
  });

  /* ---------------- 7. Sammenligning (kategorisiderne) ---------------- */

  function initCmp(root) {
    if (root.hasAttribute('data-bio-cmp-init')) return;
    root.setAttribute('data-bio-cmp-init', '');
    var pills = root.querySelectorAll('[data-bio-cmp-filter]');
    var rows = root.querySelectorAll('[data-bio-cmp-tags]');
    pills.forEach(function (p) {
      p.addEventListener('click', function () {
        var key = p.getAttribute('data-bio-cmp-filter');
        if (p.classList.contains('is-active') && key) key = '';   // klik igen = alle
        pills.forEach(function (x) {
          var on = x.getAttribute('data-bio-cmp-filter') === key;
          x.classList.toggle('is-active', on);
          x.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        rows.forEach(function (r) {
          var tags = ' ' + r.getAttribute('data-bio-cmp-tags') + ' ';
          r.classList.toggle('is-dim', !!key && tags.indexOf(' ' + key + ' ') < 0);
          r.querySelectorAll('[data-bio-cmp-tag]').forEach(function (t) {
            t.classList.toggle('is-hit', !!key && t.getAttribute('data-bio-cmp-tag') === key);
          });
        });
      });
    });
    // Rækkerne glider let ind første gang, tabellen ses.
    if (!reduceMotion && 'IntersectionObserver' in window) {
      root.classList.add('is-ready');
      var io = new IntersectionObserver(function (en) {
        if (!en[0].isIntersecting) return;
        root.classList.add('is-in');
        io.disconnect();
      }, { threshold: 0.08 });
      io.observe(root);
    }
  }

  /* ---------------- 6. Galleri ("Bruges ofte sammen med", layout fuld) ---------------- */

  function initAcc(root) {
    if (root.hasAttribute('data-bio-acc-init')) return;
    root.setAttribute('data-bio-acc-init', '');
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-bio-acc-item]'));
    var canHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;
    function open(it) {
      items.forEach(function (x) {
        var on = x === it;
        x.classList.toggle('is-open', on);
        var b = x.querySelector('.pd-bio-acc__tab');
        if (b) b.setAttribute('aria-expanded', on ? 'true' : 'false');
      });
    }
    items.forEach(function (it) {
      if (canHover) it.addEventListener('mouseenter', function () { open(it); });
      it.addEventListener('focusin', function () { open(it); });
      it.addEventListener('click', function (e) {
        if (e.target instanceof Element && e.target.closest('a')) return;   // links virker som links
        open(it);
      });
    });
  }

  /* ---------------- 5. Producentfilter ---------------- */

  function syncFilterCards() {
    var params = new URLSearchParams(window.location.search);
    document.querySelectorAll('[data-bio-filter]').forEach(function (a) {
      var on = params.getAll(a.getAttribute('data-param')).indexOf(a.getAttribute('data-value')) > -1;
      a.classList.toggle('is-active', on);
      if (on) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current');
    });
  }

  function scrollToProducts() {
    var t = document.getElementById('produkter');
    if (t) t.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }

  var filterBusy = false;
  document.addEventListener('click', function (e) {
    var a = e.target instanceof Element ? e.target.closest('[data-bio-filter]') : null;
    if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
    var grid = document.getElementById('ProductGridContainer');
    if (!grid || !window.fetch || !window.DOMParser) return; // almindeligt link som reserve
    e.preventDefault();
    if (filterBusy) return;

    var param = a.getAttribute('data-param');
    var value = a.getAttribute('data-value');
    var url = new URL(window.location.href);
    var on = url.searchParams.getAll(param).indexOf(value) > -1;
    url.searchParams.delete(param);          // én producent ad gangen
    url.searchParams.delete('page');
    if (!on) url.searchParams.append(param, value);
    url.hash = '';

    filterBusy = true;
    grid.style.transition = 'opacity .2s ease';
    grid.style.opacity = '.35';
    scrollToProducts();
    fetch(url.toString(), { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var ng = doc.getElementById('ProductGridContainer');
        if (ng) {
          grid.innerHTML = ng.innerHTML;
          // scripts i det nye gitter skal genskabes for at køre (som custom_product_filters)
          grid.querySelectorAll('script').forEach(function (old) {
            var sc = document.createElement('script');
            for (var i = 0; i < old.attributes.length; i++) sc.setAttribute(old.attributes[i].name, old.attributes[i].value);
            sc.textContent = old.textContent;
            old.parentNode.replaceChild(sc, old);
          });
        }
        document.querySelectorAll('.filters-container[data-section-id]').forEach(function (c) {
          var nc = doc.querySelector('.filters-container[data-section-id="' + c.getAttribute('data-section-id') + '"]');
          if (nc) c.innerHTML = nc.innerHTML;
          var rb = c.querySelector('.reset-filters-btn');
          if (rb) rb.classList.toggle('is-visible', url.search.indexOf('filter.') > -1);
        });
        if (window.history && history.pushState) history.pushState({}, '', url.toString());
        syncFilterCards();
      })
      .catch(function () { window.location.href = a.href; })
      .then(function () { grid.style.opacity = ''; filterBusy = false; });
  });

  // Hold kortene i takt, når andre filtre (fx "Nulstil alle filtre") skifter URL'en.
  function watchGrid() {
    var grid = document.getElementById('ProductGridContainer');
    if (!grid || !document.querySelector('[data-bio-filter]') || !window.MutationObserver) return;
    new MutationObserver(function () { syncFilterCards(); }).observe(grid, { childList: true });
    window.addEventListener('popstate', syncFilterCards);
    // Direkte link med #produkter (fx åbnet i ny fane): scroll ned, når siden er tegnet.
    if (window.location.hash === '#produkter') window.setTimeout(scrollToProducts, 300);
  }

  function initAll(scope) {
    var s = scope || document;
    s.querySelectorAll('[data-bio-dia]').forEach(initDiagram);
    s.querySelectorAll('[data-bio-guide]').forEach(initGuide);
    s.querySelectorAll('[data-bio-video]').forEach(initVideo);
    s.querySelectorAll('[data-bio-acc]').forEach(initAcc);
    s.querySelectorAll('[data-bio-cmp]').forEach(initCmp);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); watchGrid(); });
  } else {
    initAll();
    watchGrid();
  }
  // Temaeditoren gen-renderer sektioner uden genindlæsning.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
